import React, { useCallback, useEffect, useState } from "react";
import {
    Mic,
    ShoppingBag,
    Car,
    HeartPulse,
    Stethoscope,
    Wallet,
    MessageSquareHeart,
    Sun,
    Activity,
    Pill,
    CalendarClock,
    UserPlus,
    Sparkles,
    Share2,
} from "lucide-react";
import { useClara } from "../contexts/ClaraContext";
import { useData } from "../contexts/DataContext";
import {
    HOME_ACTIONS,
    TODAY_SUMMARY,
    USER,
} from "../data/sampleData";
import Avatar from "../components/Avatar";
import EmergencyCard from "../components/EmergencyCard";
import ClaraListening from "../components/ClaraListening";
import AddContactDialog from "../components/AddContactDialog";
import useSpeechRecognition from "../hooks/useSpeechRecognition";

const ICONS = {
    ShoppingBag,
    Car,
    HeartPulse,
    Stethoscope,
    Wallet,
    MessageSquareHeart,
};

const COMMAND_MAP = [
    { id: "shopping", keywords: ["shop", "buy", "order", "amazon", "target", "store"] },
    {
        id: "transport",
        keywords: ["food", "ride", "uber", "lyft", "doordash", "delivery", "transit", "bus"],
    },
    { id: "vitals", keywords: ["health", "vital", "glucose", "blood pressure", "bp", "sugar"] },
    { id: "telehealth", keywords: ["doctor", "appointment", "telehealth", "patel", "visit"] },
    { id: "bills", keywords: ["bill", "pay", "electric", "water", "phone"] },
    { id: "messages", keywords: ["message", "jenny", "tom", "walgreens", "book club"] },
    { id: "caregiver", keywords: ["caregiver", "share", "weekly", "summary"] },
];

function matchCommand(text) {
    if (!text) return null;
    const lower = text.toLowerCase();
    for (const m of COMMAND_MAP) {
        if (m.keywords.some((k) => lower.includes(k))) return m.id;
    }
    return null;
}

const RELATION_VOCAB = {
    daughter: "Daughter",
    son: "Son",
    husband: "Husband",
    wife: "Wife",
    sister: "Sister",
    brother: "Brother",
    mother: "Mother",
    mom: "Mother",
    father: "Father",
    dad: "Father",
    friend: "Friend",
    neighbor: "Neighbor",
    caregiver: "Caregiver",
    doctor: "Doctor",
};

const ADD_STOP_WORDS = new Set([
    "add",
    "my",
    "a",
    "an",
    "the",
    "please",
    "clara",
    "to",
    "for",
    "as",
    "trusted",
    "circle",
    "contact",
    "contacts",
    "new",
    "and",
]);

/**
 * Parses an "add contact" voice phrase such as:
 *   "Add my son Mike"
 *   "Add Sarah as my daughter"
 *   "Add Mike"
 * Returns { name, relation } or null if no add intent detected.
 */
function parseAddContact(text) {
    if (!text) return null;
    const lower = text.toLowerCase().trim();
    if (!/\badd\b/.test(lower)) return null;

    // Find the first matching relation word
    let relation = null;
    for (const [k, v] of Object.entries(RELATION_VOCAB)) {
        if (new RegExp(`\\b${k}\\b`, "i").test(lower)) {
            relation = v;
            break;
        }
    }

    // Strip punctuation, split, drop stop words + relation words → first remaining = name
    const tokens = text
        .replace(/[^\w\s]/g, " ")
        .split(/\s+/)
        .filter(Boolean)
        .filter(
            (w) =>
                !ADD_STOP_WORDS.has(w.toLowerCase()) &&
                !RELATION_VOCAB[w.toLowerCase()],
        );
    const first = tokens[0];
    const name = first
        ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase()
        : "";

    if (!name && !relation) return null;
    return { name, relation };
}

export const HomeScreen = ({ onNavigate }) => {
    const { speak, stop } = useClara();
    const { trustedCircle, latestGlucose, latestBp, addContact } = useData();
    const sr = useSpeechRecognition();
    const [listening, setListening] = useState(false);
    const [addingContact, setAddingContact] = useState(false);
    const [contactPrefill, setContactPrefill] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => {
            speak(
                `Good morning, ${USER.name}. I'm Clara, your voice assistant. Tap any card or the microphone to get started.`,
            );
        }, 600);
        return () => clearTimeout(t);
    }, [speak]);

    const handleResult = useCallback(
        (text, err) => {
            setListening(false);
            if (err) {
                speak("I didn't catch that. You can also tap any card to navigate.");
                return;
            }
            // Add-contact intent first (e.g., "Add my son Mike")
            if (/\badd\b/i.test(text)) {
                const parsed = parseAddContact(text);
                if (parsed) {
                    setContactPrefill(parsed);
                    setAddingContact(true);
                    const summary = [parsed.name, parsed.relation]
                        .filter(Boolean)
                        .join(", ");
                    speak(
                        `Sure. I'm opening the add contact form${summary ? ` with ${summary} filled in` : ""}. Review the details and tap Add contact.`,
                    );
                    return;
                }
            }
            const id = matchCommand(text);
            if (id) {
                const target =
                    HOME_ACTIONS.find((a) => a.id === id) ||
                    (id === "caregiver" ? { id: "caregiver", title: "Weekly summary" } : null);
                if (target) {
                    speak(`Got it. Opening ${target.title}.`);
                    onNavigate(target.id);
                    return;
                }
            }
            speak(
                `I heard "${text}". Try saying shopping, doctor, messages, bills, health vitals, weekly summary, or add my daughter Sarah.`,
            );
        },
        [onNavigate, speak],
    );

    const handleTalk = () => {
        stop();
        setListening(true);
        speak(`Hi ${USER.name}! What can I help you with today?`, {
            onEnd: () => {
                if (sr.supported) sr.start(handleResult);
            },
        });
    };

    const closeListening = () => {
        sr.stop();
        setListening(false);
        speak("Cancelled.");
    };

    const handleDemoVoice = (id) => {
        sr.stop();
        setListening(false);
        const target =
            HOME_ACTIONS.find((a) => a.id === id) ||
            (id === "caregiver" ? { id: "caregiver", title: "Weekly summary" } : null);
        if (!target) return;
        speak(`Got it. Opening ${target.title}.`);
        onNavigate(target.id);
    };

    const handleAction = (action) => {
        speak(`Opening ${action.title}. ${action.desc}.`);
        onNavigate(action.id);
    };

    const handleAddContact = (contact) => {
        addContact(contact);
        speak(`Added ${contact.name}, your ${contact.relation.toLowerCase()}, to your trusted circle.`);
    };

    return (
        <div className="space-y-7">
            {/* Welcome banner */}
            <section
                aria-label="Welcome banner"
                data-testid="welcome-banner"
                className="relative rounded-3xl overflow-hidden p-6 sm:p-7 text-white shadow-lg"
                style={{
                    background: "linear-gradient(135deg, #0A3D7A 0%, #1B5BA8 55%, #2E7AB8 100%)",
                }}
            >
                <div aria-hidden="true" className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#B8860B]/25 blur-2xl" />
                <div aria-hidden="true" className="absolute -bottom-16 -left-10 w-44 h-44 rounded-full bg-white/10 blur-2xl" />
                <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                        <Sun className="w-4 h-4 text-[#FFD580]" aria-hidden="true" />
                        <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#FFE7A8]">
                            Good morning
                        </p>
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                        Hello, {USER.name}.
                    </h1>
                    <p className="text-white/90 mt-3 text-base sm:text-lg leading-relaxed max-w-[28ch]">
                        Clara is ready to help with anything you need today.
                    </p>
                </div>
            </section>

            {/* Talk to Clara CTA */}
            <button
                type="button"
                onClick={handleTalk}
                aria-label="Tap to talk to Clara, your voice assistant"
                data-testid="talk-to-clara-btn"
                className="w-full rounded-3xl bg-[#1A1A2E] text-white px-6 py-5 flex items-center gap-4 shadow-lg hover:bg-[#262642] transition-colors active:scale-[0.99]"
            >
                <span
                    aria-hidden="true"
                    className="relative shrink-0 w-14 h-14 rounded-2xl bg-[#B8860B] flex items-center justify-center"
                >
                    <span className="absolute inset-0 rounded-2xl bg-[#B8860B] pulse-ring" />
                    <Mic className="relative w-7 h-7 text-[#1A1A2E]" strokeWidth={2.5} />
                </span>
                <span className="text-left">
                    <span className="block text-[11px] uppercase tracking-[0.2em] text-[#E0C875] font-semibold">
                        Voice assistant
                    </span>
                    <span className="block font-display text-2xl font-bold leading-tight">
                        Tap to talk to Clara
                    </span>
                </span>
                <Sparkles className="ml-auto w-5 h-5 text-[#E0C875]" aria-hidden="true" />
            </button>

            {/* Live listening overlay */}
            {listening && (
                <ClaraListening
                    title="Clara is listening"
                    subtitle={
                        sr.supported
                            ? "Say where you'd like to go. For example: messages, doctor, weekly summary, or health vitals."
                            : "Voice recognition is not supported here. Tap a demo command below."
                    }
                    liveTranscript={sr.transcript}
                    isLive={sr.isListening}
                    examples={[
                        "Open my messages",
                        "I want to call my doctor",
                        "Show my weekly summary",
                        "Add my daughter Sarah",
                    ]}
                    onCancel={closeListening}
                    demoCommands={[
                        ...HOME_ACTIONS.map((a) => ({
                            id: a.id,
                            label: a.title,
                            onClick: () => handleDemoVoice(a.id),
                        })),
                        {
                            id: "caregiver",
                            label: "Weekly summary",
                            onClick: () => handleDemoVoice("caregiver"),
                        },
                        {
                            id: "add-contact",
                            label: "Add Mike (son)",
                            onClick: () => {
                                sr.stop();
                                setListening(false);
                                setContactPrefill({ name: "Mike", relation: "Son" });
                                setAddingContact(true);
                                speak(
                                    "Sure. I'm opening the add contact form with Mike, your son, filled in. Review the details and tap Add contact.",
                                );
                            },
                        },
                    ]}
                />
            )}

            {/* Emergency Help */}
            <EmergencyCard />

            {/* Action grid */}
            <section aria-label="Quick actions">
                <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-3">
                    What would you like to do?
                </h2>
                <div className="grid grid-cols-2 gap-3.5">
                    {HOME_ACTIONS.map((a) => {
                        const Icon = ICONS[a.icon] || ShoppingBag;
                        return (
                            <button
                                key={a.id}
                                type="button"
                                onClick={() => handleAction(a)}
                                aria-label={`${a.title}: ${a.desc}`}
                                data-testid={`action-card-${a.id}`}
                                className="group text-left bg-white rounded-2xl p-4 min-h-[140px] flex flex-col items-start gap-3 border border-[#0A3D7A]/10 shadow-[0_2px_12px_rgba(10,61,122,0.06)] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(10,61,122,0.12)] hover:border-[#0A3D7A]/25 transition-all"
                            >
                                <span
                                    aria-hidden="true"
                                    className="w-11 h-11 rounded-xl bg-[#0A3D7A] flex items-center justify-center group-hover:bg-[#B8860B] transition-colors"
                                >
                                    <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
                                </span>
                                <span>
                                    <span className="block font-display font-bold text-lg text-[#1A1A2E] leading-tight">
                                        {a.title}
                                    </span>
                                    <span className="block text-sm text-[#5A5A6E] mt-0.5 leading-snug">
                                        {a.desc}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Today's Summary */}
            <section
                aria-label="Today's summary"
                data-testid="todays-summary"
                className="bg-white rounded-3xl p-5 sm:p-6 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)]"
            >
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-xl font-bold text-[#1A1A2E]">
                        Today's summary
                    </h2>
                    <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#B8860B]">
                        Live
                    </span>
                </div>
                <ul className="space-y-3">
                    <SummaryRow
                        Icon={Activity}
                        label="Glucose"
                        value={`${latestGlucose.value} mg/dL`}
                        tag={TODAY_SUMMARY.glucose.status}
                        tagColor="bg-[#E5F1EB] text-[#0A5C36]"
                        testId="summary-glucose"
                    />
                    <SummaryRow
                        Icon={HeartPulse}
                        label="Last BP reading"
                        value={TODAY_SUMMARY.bp.lastReadingAt}
                        tag={`${latestBp.systolic}/${latestBp.diastolic}`}
                        tagColor="bg-[#E8EEF7] text-[#0A3D7A]"
                        testId="summary-bp"
                    />
                    <SummaryRow
                        Icon={Pill}
                        label="Next medication"
                        value={`${TODAY_SUMMARY.nextMedication.name} · ${TODAY_SUMMARY.nextMedication.time}`}
                        tag="Reminder set"
                        tagColor="bg-[#F0EAD6] text-[#7A5A0A]"
                        testId="summary-meds"
                    />
                    <SummaryRow
                        Icon={CalendarClock}
                        label="Next appointment"
                        value={`${TODAY_SUMMARY.nextAppointment.doctor}`}
                        tag={TODAY_SUMMARY.nextAppointment.when}
                        tagColor="bg-[#E8EEF7] text-[#0A3D7A]"
                        testId="summary-appointment"
                    />
                </ul>
            </section>

            {/* Trusted Circle */}
            <section
                aria-label="Trusted circle"
                data-testid="trusted-circle"
                className="bg-white rounded-3xl p-5 sm:p-6 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)]"
            >
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-xl font-bold text-[#1A1A2E]">
                        Trusted Circle
                    </h2>
                    <span className="text-xs text-[#5A5A6E]">
                        Family & care helpers
                    </span>
                </div>
                <div
                    className="flex items-center gap-4 flex-wrap"
                    data-testid="trusted-circle-list"
                >
                    {trustedCircle.map((p) => (
                        <div
                            key={p.id}
                            className="flex flex-col items-center gap-1.5"
                            data-testid={`trusted-${p.id}`}
                        >
                            <Avatar
                                seed={p.avatarSeed}
                                name={`${p.name}, ${p.relation}`}
                                size={64}
                            />
                            <p className="text-sm font-semibold text-[#1A1A2E] leading-tight">
                                {p.name}
                            </p>
                            <p className="text-xs text-[#5A5A6E] leading-tight">
                                {p.relation}
                            </p>
                        </div>
                    ))}
                    <button
                        type="button"
                        aria-label="Add a new trusted contact"
                        data-testid="trusted-add-btn"
                        onClick={() => {
                            speak("Who would you like to add to your trusted circle?");
                            setAddingContact(true);
                        }}
                        className="flex flex-col items-center gap-1.5"
                    >
                        <span className="w-16 h-16 rounded-full border-2 border-dashed border-[#0A3D7A]/50 text-[#0A3D7A] flex items-center justify-center hover:bg-[#0A3D7A]/5 transition-colors">
                            <UserPlus className="w-6 h-6" aria-hidden="true" />
                        </span>
                        <p className="text-sm font-semibold text-[#0A3D7A] leading-tight">
                            Add
                        </p>
                    </button>
                </div>

                <button
                    type="button"
                    onClick={() => {
                        speak("Opening your weekly summary for caregivers.");
                        onNavigate("caregiver");
                    }}
                    data-testid="share-weekly-summary-btn"
                    aria-label="View weekly summary to share with your caregivers"
                    className="mt-5 w-full min-h-[52px] rounded-2xl border-2 border-[#0A3D7A] text-[#0A3D7A] font-bold flex items-center justify-center gap-2 hover:bg-[#0A3D7A]/5 transition-colors"
                >
                    <Share2 className="w-5 h-5" aria-hidden="true" />
                    Share weekly summary
                </button>
            </section>

            <AddContactDialog
                open={addingContact}
                onClose={() => {
                    setAddingContact(false);
                    setContactPrefill(null);
                }}
                onAdd={handleAddContact}
                initialValues={contactPrefill}
            />
        </div>
    );
};

const SummaryRow = ({ Icon, label, value, tag, tagColor, testId }) => (
    <li
        className="flex items-center gap-3 py-2 border-b border-[#F0EAD6] last:border-b-0"
        data-testid={testId}
    >
        <span
            aria-hidden="true"
            className="shrink-0 w-10 h-10 rounded-xl bg-[#F0EAD6] flex items-center justify-center"
        >
            <Icon className="w-5 h-5 text-[#0A3D7A]" strokeWidth={2.2} />
        </span>
        <div className="flex-1 min-w-0">
            <p className="text-xs text-[#5A5A6E] uppercase tracking-wider font-semibold">
                {label}
            </p>
            <p className="text-base font-semibold text-[#1A1A2E] truncate">
                {value}
            </p>
        </div>
        {tag && (
            <span className={`shrink-0 text-xs font-bold rounded-full px-3 py-1 ${tagColor}`}>
                {tag}
            </span>
        )}
    </li>
);

export default HomeScreen;
